import React from "react";

export type ArtifactWorkbenchRow = {
  artifactId: string;
  channel: string;
  detectorId: string;
  startSeconds: number;
  endSeconds: number;
  decision: "pending" | "accept" | "reject" | "reprocess";
  beforeTraceHash?: string;
  afterTraceHash?: string;
  reprocessQueued?: boolean;
};

export type ArtifactReviewDashboardProps = {
  recordingId: string;
  items: ArtifactWorkbenchRow[];
  onDecide: (artifactId: string, decision: "accept" | "reject" | "reprocess") => void;
};

export default function ArtifactReviewDashboard({
  recordingId,
  items,
  onDecide,
}: ArtifactReviewDashboardProps) {
  const decided = items.filter((item) => item.decision !== "pending").length;
  const reprocessCount = items.filter((item) => item.decision === "reprocess").length;
  const evidenceReady = items.length > 0 && items.every((item) => {
    if (item.decision === "pending") return false;
    if (!item.beforeTraceHash || !item.afterTraceHash) return false;
    if (item.decision === "reprocess") return item.reprocessQueued === true;
    return true;
  });

  return (
    <section
      className="artifact-review-dashboard"
      data-testid="artifact-review-dashboard"
      aria-label="Artifact workbench"
    >
      <h3 className="artifact-review-dashboard__title">Artifact workbench</h3>
      <p className="artifact-review-dashboard__meta">Recording {recordingId}</p>
      <div className="d-flex flex-wrap gap-2 mb-2" aria-label="Artifact workbench readiness">
        <span className={`badge ${evidenceReady ? "bg-success" : "bg-warning text-dark"}`}>
          {evidenceReady ? "Evidence ready" : "Evidence pending"}
        </span>
        <span className="badge bg-secondary">{decided}/{items.length} decided</span>
        <span className="badge bg-secondary">{reprocessCount} reprocess</span>
      </div>
      <table className="artifact-review-dashboard__table">
        <thead>
          <tr>
            <th>Channel</th>
            <th>Detector</th>
            <th>Interval</th>
            <th>Before / after</th>
            <th>Decision</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.artifactId}>
              <td>{item.channel}</td>
              <td>{item.detectorId}</td>
              <td>
                {item.startSeconds}s-{item.endSeconds}s
              </td>
              <td>
                <code>{item.beforeTraceHash || "N/A"}</code>
                {" -> "}
                <code>{item.afterTraceHash || "N/A"}</code>
                {item.decision === "reprocess" && (
                  <div className="artifact-review-dashboard__queue">
                    {item.reprocessQueued ? "Queue ready" : "Queue pending"}
                  </div>
                )}
              </td>
              <td>
                <div className="btn-group btn-group-sm">
                  <button type="button" className="btn btn-outline-success" onClick={() => onDecide(item.artifactId, "accept")}>
                    Accept
                  </button>
                  <button type="button" className="btn btn-outline-danger" onClick={() => onDecide(item.artifactId, "reject")}>
                    Reject
                  </button>
                  <button type="button" className="btn btn-outline-secondary" onClick={() => onDecide(item.artifactId, "reprocess")}>
                    Reprocess
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
